import { Eye } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { shippixService } from '../../services/shippixService';
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip
} from '@mui/material';
import { Search, Add, Visibility as VisibilityIcon, LocalPrintshop as LocalPrintshopIcon, Receipt as ReceiptIcon } from '@mui/icons-material';
import { formatDate } from '../../utils/formatDate';
import { formatPrice } from '../../utils/formatPrice';
import StatusChip from '../../components/StatusChip';
import ShipmentDetailsModal from '../../components/shipping/ShipmentDetailsModal';
import PriceDisplay from '../../components/PriceDisplay';
import { format, addDays, isWeekend } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { collection, query, where, getDocs, addDoc, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useToast } from '../../contexts/ToastContext';
import { SelectChangeEvent } from '@mui/material';
import debounce from 'lodash/debounce';

export default function MyShipments() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [allShipments, setAllShipments] = useState<any[]>([]);
  const [filteredShipments, setFilteredShipments] = useState<any[]>([]);
  const [shipments, setShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [rowsPerPage] = useState(25); 
  const [needsConfig, setNeedsConfig] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<ShippixShipment | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [availableStatuses, setAvailableStatuses] = useState<Array<{ id: string; name: string }>>([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [refundedShipments, setRefundedShipments] = useState<Set<string>>(new Set());
  const [refundChecked, setRefundChecked] = useState(false);
  const { show } = useToast();

  // Função para verificar envios anulados e processar reembolsos
  const checkCanceledShipments = useCallback(async (shipments: ShippixShipment[]) => {
    if (refundChecked) return; // Se já verificamos, não verifica novamente

    console.log('Verificando envios anulados:', shipments);
    const canceledShipments = shipments.filter(s => 
      s.status?.id === 'canceled' || 
      s.status?.id === 'anulado' || 
      s.status?.name?.toLowerCase().includes('anulado')
    );
    
    console.log('Envios anulados encontrados:', canceledShipments);
    
    if (canceledShipments.length === 0) {
      setRefundChecked(true);
      return;
    }

    // Primeiro verificar quais envios já foram verificados anteriormente
    const verifiedRef = collection(db, 'verifiedShipments');
    const verifiedShipments = new Set<string>();
    
    // Buscar em lotes de 10
    const batchSize = 10;
    for (let i = 0; i < canceledShipments.length; i += batchSize) {
      const batchShipments = canceledShipments.slice(i, i + batchSize);
      const verifiedQuery = query(verifiedRef,
        where('userId', '==', currentUser.uid),
        where('shipmentId', 'in', batchShipments.map(s => s.id))
      );
      const verifiedDocs = await getDocs(verifiedQuery);
      verifiedDocs.forEach(doc => {
        verifiedShipments.add(doc.data().shipmentId);
      });
    }

    // Buscar transações apenas para envios não verificados
    const unverifiedShipments = canceledShipments.filter(s => !verifiedShipments.has(s.id));
    const trackingCodes = unverifiedShipments.map(s => s.tracking_code);
    
    if (trackingCodes.length === 0) {
      setRefundChecked(true);
      return;
    }

    // Buscar transações em lotes
    const transactionsRef = collection(db, 'walletTransactions');
    const refundedIds = new Set<string>();

    for (let i = 0; i < trackingCodes.length; i += batchSize) {
      const batchCodes = trackingCodes.slice(i, i + batchSize);
      const debitQuery = query(transactionsRef,
        where('userId', '==', currentUser.uid),
        where('trackingCode', 'in', batchCodes),
        where('type', '==', 'shipping_payment'),
        where('status', '==', 'completed')
      );
      
      const debitDocs = await getDocs(debitQuery);
      const debitTransactions = new Map(
        debitDocs.docs.map(doc => [doc.data().trackingCode, doc.data()])
      );

      // Para cada envio não verificado neste lote
      for (const shipment of unverifiedShipments.filter(s => batchCodes.includes(s.tracking_code))) {
        const debitTransaction = debitTransactions.get(shipment.tracking_code);
        
        if (!debitTransaction) {
          console.log(`Nenhuma transação de débito encontrada para o envio ${shipment.tracking_code}. Registrando como verificado.`);
          // Registrar que este envio foi verificado e não tinha transação
          await addDoc(collection(db, 'verifiedShipments'), {
            userId: currentUser.uid,
            shipmentId: shipment.id,
            trackingCode: shipment.tracking_code,
            verifiedAt: new Date().toISOString(),
            status: 'no_transaction'
          });
          continue;
        }

        // Verificar se já foi reembolsado
        const refundQuery = query(collection(db, 'shipmentRefunds'),
          where('userId', '==', currentUser.uid),
          where('shipmentId', '==', shipment.id)
        );
        const refundDocs = await getDocs(refundQuery);

        if (!refundDocs.empty) {
          console.log(`Envio ${shipment.id} já foi reembolsado anteriormente`);
          refundedIds.add(shipment.id);
          continue;
        }

        // Processar reembolso
        try {
          const refundAmount = Math.abs(debitTransaction.amount);

          // Criar transação de reembolso
          const refundTransaction = {
            userId: currentUser.uid,
            companyId: currentUser.companyId,
            amount: refundAmount,
            type: 'shipping_refund',
            source: 'shipping',
            status: 'completed',
            trackingCode: shipment.tracking_code,
            description: `Reembolso automático - Envio ${shipment.tracking_code} anulado`,
            createdAt: new Date().toISOString()
          };

          await addDoc(collection(db, 'walletTransactions'), refundTransaction);

          // Atualizar saldo da wallet
          const walletRef = doc(db, 'wallets', currentUser.uid);
          const walletDoc = await getDoc(walletRef);
          
          if (walletDoc.exists()) {
            const currentBalance = walletDoc.data().balance || 0;
            await updateDoc(walletRef, {
              balance: currentBalance + refundAmount
            });
          }

          // Registrar o reembolso
          await addDoc(collection(db, 'shipmentRefunds'), {
            userId: currentUser.uid,
            shipmentId: shipment.id,
            amount: refundAmount,
            createdAt: Timestamp.now()
          });

          // Registrar que este envio foi verificado e reembolsado
          await addDoc(collection(db, 'verifiedShipments'), {
            userId: currentUser.uid,
            shipmentId: shipment.id,
            trackingCode: shipment.tracking_code,
            verifiedAt: new Date().toISOString(),
            status: 'refunded',
            amount: refundAmount
          });

          refundedIds.add(shipment.id);
          show(`Reembolso de ${formatPrice(refundAmount)} processado com sucesso para o envio ${shipment.tracking_code}`, 'success');
        } catch (error) {
          console.error('Erro ao processar reembolso:', error);
          show('Erro ao processar reembolso. Por favor, contacte o suporte.', 'error');
        }
      }
    }

    // Atualizar estado com todos os envios reembolsados
    const allRefundedIds = new Set([...verifiedShipments, ...refundedIds]);
    setRefundedShipments(allRefundedIds);
    setRefundChecked(true);
  }, [currentUser?.uid, currentUser?.companyId, show, refundChecked]);

  // Aplicar filtros quando mudar busca ou status
  useEffect(() => {
    if (allShipments.length > 0) {
      // Primeiro filtrar recolhas (tracking com "/")
      let filtered = allShipments.filter(shipment => 
        !shipment.tracking_code?.includes('/')
      );
      
      // Por defeito, ocultar envios anulados a não ser que estejam explicitamente selecionados
      if (!selectedStatus || selectedStatus !== 'anulado') {
        filtered = filtered.filter(shipment => 
          shipment.status?.id !== 'anulado' && 
          !shipment.status?.name?.toLowerCase().includes('anulado') &&
          shipment.status?.id !== 'canceled'
        );
      }
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(shipment => 
          shipment.tracking_code?.toLowerCase().includes(query) ||
          shipment.recipient_name?.toLowerCase().includes(query)
        );
      }

      if (selectedStatus) {
        if (selectedStatus === 'anulado') {
          filtered = allShipments.filter(shipment => 
            !shipment.tracking_code?.includes('/') && (
              shipment.status?.id === 'anulado' || 
              shipment.status?.name?.toLowerCase().includes('anulado') ||
              shipment.status?.id === 'canceled'
            )
          );
        } else {
          filtered = filtered.filter(shipment => 
            shipment.status?.id === selectedStatus
          );
        }
      }

      setFilteredShipments(filtered);
      setTotalItems(filtered.length);

      // Paginar os resultados filtrados
      const start = page * rowsPerPage;
      const end = start + rowsPerPage;
      setShipments(filtered.slice(start, end));
    }
  }, [allShipments, searchQuery, selectedStatus, page, rowsPerPage]);

  const loadShipments = useCallback(async () => {
    try {
      if (!currentUser?.companyId) {
        setError('Empresa não encontrada');
        setLoading(false);
        return;
      }

      // Primeiro verificar se existem credenciais configuradas
      const config = await shippixService.getConfig(currentUser.companyId);
      if (!config) {
        setError('Configurações do Shippix não encontradas. Por favor, configure suas credenciais primeiro.');
        setNeedsConfig(true);
        return;
      }

      setLoading(true);
      setError('');
      setNeedsConfig(false);
      
      const response = await shippixService.listShipments(
        currentUser.companyId,
        1,
        1000 // Buscar todos os envios de uma vez
      );

      console.log('Resposta da API:', response);

      // Verifica se a resposta é um array (formato antigo) ou um objeto com propriedade shipments
      const shipments = Array.isArray(response) ? response : response?.shipments || [];
      
      if (shipments.length > 0) {
        // Extrair estados únicos dos envios e garantir que "Anulado" está sempre disponível
        const statusMap = new Map([
          ['', { id: '', name: 'Todos os estados' }],
          ...shipments
            .filter(shipment => 
              shipment.status?.id !== 'anulado' && 
              !shipment.status?.name?.toLowerCase().includes('anulado') &&
              shipment.status?.id !== 'canceled'
            )
            .map(shipment => [
              shipment.status?.id || '',
              { id: shipment.status?.id || '', name: shipment.status?.name || 'Desconhecido' }
            ])
        ]);

        // Adicionar estado "Anulado" após filtrar os outros estados
        statusMap.set('anulado', { id: 'anulado', name: 'Anulado' });
        
        const uniqueStatuses = Array.from(statusMap.values())
          .sort((a, b) => a.name.localeCompare(b.name));
        
        setAvailableStatuses(uniqueStatuses);
        
        setAllShipments(shipments);
        
        // Verificar envios anulados após carregar a lista
        if (!refundChecked) {
          await checkCanceledShipments(shipments);
        }
      } else {
        console.warn('Nenhum envio encontrado na resposta:', response);
        setAllShipments([]);
        setFilteredShipments([]);
        setShipments([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Erro ao carregar envios:', error);
      setError('Erro ao carregar envios. Por favor, tente novamente.');
      setAllShipments([]);
      setFilteredShipments([]);
      setShipments([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }, [currentUser?.companyId, checkCanceledShipments, refundChecked]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value);
    }, 300),
    []
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  const handleStatusChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedStatus(event.target.value);
  }, []);

  // Carregar envios iniciais
  useEffect(() => {
    loadShipments();
  }, [loadShipments]);

  // Resetar para primeira página quando mudar filtros
  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedStatus]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleViewDetails = (shipment: ShippixShipment) => {
    setSelectedShipment(shipment);
  };

  const handlePrintLabel = async (tracking: string) => {
    try {
      if (!currentUser?.companyId) {
        throw new Error('CompanyId não encontrado. Por favor, faça login novamente.');
      }
      const labelBase64 = await shippixService.getShipmentLabel(tracking, currentUser.companyId);
      const blob = base64ToBlob(labelBase64);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Erro ao imprimir etiqueta:', error);
    }
  };

  const handlePrintGuide = async (tracking: string) => {
    try {
      if (!currentUser?.companyId) {
        throw new Error('CompanyId não encontrado. Por favor, faça login novamente.');
      }
      const guideBase64 = await shippixService.getShipmentGuide(tracking, currentUser.companyId);
      const blob = base64ToBlob(guideBase64);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Erro ao imprimir guia:', error);
    }
  };

  const base64ToBlob = (base64: string, type: string = 'application/pdf'): Blob => {
    try {
      // Remove any potential data URL prefix
      const base64Data = base64.replace(/^data:[^;]+;base64,/, '');
      
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type });
    } catch (error) {
      console.error('Erro ao converter base64 para Blob:', error);
      throw new Error('Erro ao processar o documento. Formato inválido.');
    }
  };

  const getExpectedDeliveryDate = (deliveryDate: string) => {
    let date = new Date(deliveryDate);
    
    // Se a data cair num fim de semana, adiciona dias até ser dia útil
    while (isWeekend(date)) {
      date = addDays(date, 1);
    }
    
    return format(date, 'dd/MM/yyyy', { locale: ptBR });
  };

  return (
    <Card>
      <Box p={3}>
        {needsConfig ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Configure suas credenciais do Shippix</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowConfigModal(true)}
              sx={{ 
                bgcolor: 'rgb(10,25,47)', 
                '&:hover': { bgcolor: 'rgb(10,25,47)' }, 
                color: 'white'
              }}
            >
              Configurar Agora
            </Button>
          </Box>
        ) : (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5">Meus Envios</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={loadShipments}
                  sx={{ 
                    borderColor: 'rgb(10,25,47)', 
                    color: 'rgb(10,25,47)',
                    '&:hover': { 
                      borderColor: 'rgb(10,25,47)',
                      backgroundColor: 'rgba(10,25,47,0.1)'
                    }
                  }}
                >
                  Atualizar envios
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/dashboard/shipping/create')}
                  sx={{ 
                    bgcolor: 'rgb(10,25,47)', 
                    '&:hover': { bgcolor: 'rgb(10,25,47)' }, 
                    color: 'white'
                  }}
                >
                  Criar Envio
                </Button>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2} mb={3}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <TextField
                    placeholder="Pesquisar por tracking ou destinatário"
                    value={searchInputValue}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 200 }}>
                  <Select
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    displayEmpty
                    size="small"
                  >
                    {availableStatuses.map((status) => (
                      <MenuItem key={status.id} value={status.id}>
                        {status.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Stack>

            {error && (
              <Typography color="error" align="center">
                {error}
              </Typography>
            )}

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table sx={{ minWidth: 650 }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data de Criação</TableCell>
                    <TableCell>Tracking</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Destinatário</TableCell>
                    <TableCell>Operador</TableCell>
                    <TableCell>Entrega Prevista</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="center">Reembolso</TableCell>
                    <TableCell align="center">Ação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && !pageLoading && Array.isArray(shipments) && shipments.map((shipment) => (
                    <TableRow key={shipment.id} hover>
                      <TableCell>
                        {format(new Date(shipment.date), 'dd/MM/yyyy', { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => handleViewDetails(shipment)}
                          sx={{ 
                            textTransform: 'none',
                            color: '#2563EB',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {shipment.tracking_code}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip 
                            label={shipment.status?.name || 'Desconhecido'} 
                            color={shipment.status?.id === 'canceled' || shipment.status?.id === 'anulado' ? 'error' : 'default'}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{shipment.recipient_name}</TableCell>
                      <TableCell>{shipment.service.name}</TableCell>
                      <TableCell>
                        {shipment.delivery_date ? getExpectedDeliveryDate(shipment.delivery_date) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <PriceDisplay 
                          basePrice={shipment.total_price}
                          zipCode={shipment.recipient_zip_code}
                          serviceCode={shipment.service.code}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {(shipment.status?.id === 'canceled' || 
                          shipment.status?.id === 'anulado' || 
                          shipment.status?.name?.toLowerCase().includes('anulado')
                        ) && (
                          <Chip 
                            label={refundedShipments.has(shipment.id) ? 'Reembolsado' : 'Pendente'} 
                            color={refundedShipments.has(shipment.id) ? 'success' : 'warning'}
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          justifyContent: 'center',
                          flexWrap: 'nowrap',
                          '& > button': {
                            minWidth: 'auto',
                            p: 1
                          }
                        }}>
                          <Tooltip title="Ver Detalhes">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(shipment)}
                              sx={{ color: 'rgb(10,25,47)' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Imprimir Etiqueta">
                            <IconButton
                              size="small"
                              onClick={() => handlePrintLabel(shipment.tracking_code)}
                              sx={{ color: 'rgb(10,25,47)' }}
                            >
                              <LocalPrintshopIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Imprimir Guia">
                            <IconButton
                              size="small"
                              onClick={() => handlePrintGuide(shipment.tracking_code)}
                              sx={{ color: 'rgb(10,25,47)' }}
                            >
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && !pageLoading && (!shipments || shipments.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                        <Typography sx={{ color: '#6B7280' }}>
                          Nenhum envio encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[25]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              labelRowsPerPage="Linhas por página:"
            />

          </>
        )}

        {selectedShipment &&
          <ShipmentDetailsModal
            open={true}
            onClose={() => setSelectedShipment(null)}
            shipment={selectedShipment}
          />
        }
      </Box>
    </Card>
  );
}
